/**
 * VIN Decoder Utility (Offline-first)
 * 
 * VIN Structure (17 characters):
 * 1-3: World Manufacturer Identifier (WMI)
 * 4-8: Vehicle Descriptor Section (VDS)
 * 9: Check digit
 * 10: Model year
 * 11: Plant code
 * 12-17: Sequential number
 */

// World Manufacturer Identifiers (Common Indian & Global)
const WMI_CODES = {
  // Indian Manufacturers
  'MA1': { manufacturer: 'Mahindra', country: 'India' },
  'MA3': { manufacturer: 'Suzuki (Maruti)', country: 'India' },
  'MA6': { manufacturer: 'GM India', country: 'India' },
  'MA7': { manufacturer: 'Mitsubishi India', country: 'India' },
  'MAJ': { manufacturer: 'Suzuki (Maruti)', country: 'India' },
  'MAK': { manufacturer: 'Honda India', country: 'India' },
  'MAL': { manufacturer: 'Hyundai India', country: 'India' },
  'MAT': { manufacturer: 'Tata Motors', country: 'India' },
  'MBH': { manufacturer: 'Bajaj', country: 'India' },
  'MBJ': { manufacturer: 'Toyota Kirloskar', country: 'India' },
  'MBL': { manufacturer: 'Maruti Suzuki', country: 'India' },
  'MC2': { manufacturer: 'Volvo India', country: 'India' },
  'MCB': { manufacturer: 'Toyota Kirloskar', country: 'India' },
  'MCC': { manufacturer: 'Fiat India', country: 'India' },
  'MCL': { manufacturer: 'Renault India', country: 'India' },
  'MDH': { manufacturer: 'Nissan India', country: 'India' },
  'ME1': { manufacturer: 'Kia India', country: 'India' },
  'ME3': { manufacturer: 'Ford India', country: 'India' },
  'ME4': { manufacturer: 'Skoda India', country: 'India' },
  'MEC': { manufacturer: 'Daimler India', country: 'India' },
  'MEE': { manufacturer: 'Volkswagen India', country: 'India' },
  'MNA': { manufacturer: 'MG Motor India', country: 'India' },
  'MNB': { manufacturer: 'Citroën India', country: 'India' },
  
  // Japanese
  'JHM': { manufacturer: 'Honda', country: 'Japan' },
  'JT': { manufacturer: 'Toyota', country: 'Japan' },
  'JN': { manufacturer: 'Nissan', country: 'Japan' },
  'JMZ': { manufacturer: 'Mazda', country: 'Japan' },
  
  // Korean
  'KMH': { manufacturer: 'Hyundai', country: 'South Korea' },
  'KNA': { manufacturer: 'Kia', country: 'South Korea' },
  
  // German
  'WVW': { manufacturer: 'Volkswagen', country: 'Germany' },
  'WAU': { manufacturer: 'Audi', country: 'Germany' },
  'WBA': { manufacturer: 'BMW', country: 'Germany' },
  'WDB': { manufacturer: 'Mercedes-Benz', country: 'Germany' },
  'WP0': { manufacturer: 'Porsche', country: 'Germany' },
  
  // American
  '1G': { manufacturer: 'General Motors', country: 'USA' },
  '1F': { manufacturer: 'Ford', country: 'USA' },
  '1C': { manufacturer: 'Chrysler', country: 'USA' },
  '5Y': { manufacturer: 'Toyota USA', country: 'USA' },
};

// Model Year Codes (Position 10)
const YEAR_CODES = {
  'A': 2010, 'B': 2011, 'C': 2012, 'D': 2013, 'E': 2014,
  'F': 2015, 'G': 2016, 'H': 2017, 'J': 2018, 'K': 2019,
  'L': 2020, 'M': 2021, 'N': 2022, 'P': 2023, 'R': 2024,
  'S': 2025, 'T': 2026, 'V': 2027, 'W': 2028, 'X': 2029,
  'Y': 2030, '1': 2031, '2': 2032, '3': 2033, '4': 2034,
  '5': 2035, '6': 2036, '7': 2037, '8': 2038, '9': 2039,
};

// Plant codes for major manufacturers
const PLANT_CODES = {
  // Maruti Suzuki
  'MAJ': {
    'G': 'Gurgaon, Haryana',
    'M': 'Manesar, Haryana',
    'S': 'SMG Gujarat',
  },
  'MBL': {
    'G': 'Gurgaon, Haryana',
    'M': 'Manesar, Haryana',
  },
  // Hyundai
  'MAL': {
    'S': 'Sriperumbudur, Tamil Nadu',
    'T': 'Sriperumbudur Plant 2',
  },
  // Tata
  'MAT': {
    'P': 'Pune, Maharashtra',
    'S': 'Sanand, Gujarat',
    'J': 'Jamshedpur, Jharkhand',
    'L': 'Lucknow, UP',
    'D': 'Dharwad, Karnataka',
  },
  // Toyota
  'MBJ': {
    'B': 'Bidadi, Karnataka',
  },
  // Kia
  'ME1': {
    'A': 'Anantapur, Andhra Pradesh',
  },
  // Honda
  'MAK': {
    'T': 'Tapukara, Rajasthan',
    'G': 'Greater Noida, UP',
  },
  // Mahindra
  'MA1': {
    'N': 'Nashik, Maharashtra',
    'C': 'Chakan, Maharashtra',
    'Z': 'Zaheerabad, Telangana',
  },
};

/**
 * Validates VIN format
 */
export const validateVIN = (vin) => {
  if (!vin) return { valid: false, error: 'VIN is required' };
  
  const cleanVIN = vin.toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/g, '');
  
  if (cleanVIN.length !== 17) {
    return { valid: false, error: `VIN must be 17 characters (got ${cleanVIN.length})` };
  }
  
  // Check for invalid characters (I, O, Q are not used)
  if (/[IOQ]/i.test(cleanVIN)) {
    return { valid: false, error: 'VIN cannot contain I, O, or Q' };
  }
  
  return { valid: true, vin: cleanVIN };
};

/**
 * Decodes VIN and returns vehicle information
 */
export const decodeVIN = (vin) => {
  const validation = validateVIN(vin);
  if (!validation.valid) {
    return { success: false, error: validation.error };
  }
  
  const cleanVIN = validation.vin;
  
  // Extract WMI (first 3 characters)
  const wmi3 = cleanVIN.substring(0, 3);
  const wmi2 = cleanVIN.substring(0, 2);
  
  // Find manufacturer
  let manufacturerInfo = WMI_CODES[wmi3] || WMI_CODES[wmi2] || null;
  
  // Extract model year (position 10)
  const yearCode = cleanVIN.charAt(9);
  const modelYear = YEAR_CODES[yearCode] || null;
  
  // Extract plant code (position 11)
  const plantCode = cleanVIN.charAt(10);
  let plantLocation = null;
  
  if (manufacturerInfo && PLANT_CODES[wmi3]) {
    plantLocation = PLANT_CODES[wmi3][plantCode] || 'Unknown Plant';
  }
  
  // Calculate vehicle age
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  let ageWarning = null;
  let ageMonths = null;
  
  if (modelYear) {
    // Model year typically starts in Q4 of previous calendar year
    // A 2025 model year car could be manufactured from Sep 2024
    const estimatedManufactureYear = modelYear - 1;
    ageMonths = (currentYear - estimatedManufactureYear) * 12 + currentMonth;
    
    if (currentYear > modelYear) {
      ageWarning = 'old_stock';
    } else if (currentYear === modelYear && currentMonth > 6) {
      // If it's late in the year and still current model year, might be early production
      ageWarning = 'check_date';
    }
  }
  
  // Sequential number
  const serialNumber = cleanVIN.substring(11);
  
  return {
    success: true,
    vin: cleanVIN,
    manufacturer: manufacturerInfo?.manufacturer || 'Unknown',
    country: manufacturerInfo?.country || 'Unknown',
    modelYear,
    yearCode,
    plantCode,
    plantLocation,
    serialNumber,
    ageMonths,
    ageWarning,
    wmi: wmi3,
  };
};

/**
 * Compares VIN date with invoice date
 */
export const checkDateMismatch = (vinData, invoiceDate) => {
  if (!vinData.modelYear || !invoiceDate) {
    return { mismatch: false, warning: null };
  }
  
  const invoice = new Date(invoiceDate);
  const invoiceYear = invoice.getFullYear();
  const invoiceMonth = invoice.getMonth() + 1;
  
  const warnings = [];
  
  // Model year is typically ahead of calendar year
  // e.g., 2025 model year cars are sold from late 2024
  if (vinData.modelYear < invoiceYear) {
    warnings.push({
      type: 'old_model_year',
      message: `Vehicle is ${invoiceYear - vinData.modelYear} year(s) old model year`,
      severity: 'high',
    });
  }
  
  // If buying in late year but getting current year model (not next year)
  if (invoiceMonth >= 10 && vinData.modelYear === invoiceYear) {
    warnings.push({
      type: 'not_latest',
      message: 'Newer model year vehicles may be available',
      severity: 'medium',
    });
  }
  
  return {
    mismatch: warnings.length > 0,
    warnings,
  };
};

/**
 * Get old stock warning message
 */
export const getOldStockWarning = (vinData) => {
  if (!vinData.modelYear) return null;
  
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  
  if (vinData.modelYear < currentYear) {
    const yearsOld = currentYear - vinData.modelYear;
    return {
      isOldStock: true,
      yearsOld,
      message: `⚠️ This is a ${vinData.modelYear} model year vehicle (${yearsOld} year${yearsOld > 1 ? 's' : ''} old model)`,
      details: [
        'Battery may have degraded from sitting',
        'Tyres rubber may have aged',
        'Fluids may need replacement',
        'You may miss out on updated features',
        'Negotiate a significant discount',
      ],
    };
  }
  
  // Same model year but manufactured early
  if (vinData.modelYear === currentYear && currentMonth > 6) {
    return {
      isOldStock: false,
      potentiallyOld: true,
      message: 'Check the exact manufacturing date on the door jamb sticker',
      details: [
        'Manufacturing date should ideally be within 3-6 months',
        'Ask dealer for production date if not visible',
      ],
    };
  }
  
  return null;
};

/**
 * Format VIN for display (groups of characters)
 */
export const formatVIN = (vin) => {
  if (!vin) return '';
  const clean = vin.toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/g, '');
  // Format as: WMI-VDS-VIS (3-6-8)
  return `${clean.substring(0, 3)}-${clean.substring(3, 9)}-${clean.substring(9)}`;
};

export default {
  validateVIN,
  decodeVIN,
  checkDateMismatch,
  getOldStockWarning,
  formatVIN,
};
